import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { Issue } from "contracts/api/issue/responses";
import { ORIGIN_RESOURCE_PATHS } from "contracts/api/yeet/origin-subjects";
import { ORIGIN_CONDITION } from "contracts/enums/issue-semantics";
import { ORIGIN_FILE_KIND } from "contracts/enums/scrape";
import { contentFitnessSubcriterionForKey } from "contracts/report/content-fitness/lookups";
import { isOpen } from "../website/issueModel";
import { uniqueCurrentPagesForIssue } from "./issuePageResolution";
import {
  clientPriorityFamilyKey,
  compareIssuePresentationPriority,
  type IssuePresentationPriority,
  presentationPriorityForIssue,
  rankIssuesForClientPriority,
} from "./issuePresentationPriority";
import { originConditionFact } from "./issueSemantics";
import {
  clientIssueTitle,
  issueDimensionLabel,
  suggestedActionForIssueInSite,
} from "./issueText";

export type SummaryPriorityFamily = {
  key: string;
  title: string;
  severity: Issue["severity"];
  representative: Issue;
  relatedIssueCount: number;
  dimension: string;
  scope: string;
  action: string;
};

type FamilyAccumulator = {
  key: string;
  issues: Issue[];
  representative: Issue;
  dimensionKey: string;
  priority: IssuePresentationPriority;
};

const DIVERSITY_SCORE_WINDOW = 40;

export function summaryPriorityFamilies(
  data: OverviewResponse,
  limit = 4,
): SummaryPriorityFamily[] {
  const openIssues = data.issues.filter(isOpen);
  const grouped = groupPriorityIssues(data, openIssues);
  const selected: FamilyAccumulator[] = [];
  const usedDimensions = new Set<string>();

  const remaining = [...grouped];
  while (remaining.length > 0 && selected.length < limit) {
    const strongest = remaining[0];
    if (!strongest) break;
    const diverseIndex = remaining.findIndex(
      (family) =>
        !usedDimensions.has(family.dimensionKey) &&
        similarlyImportant(strongest, family),
    );
    const [family] = remaining.splice(diverseIndex >= 0 ? diverseIndex : 0, 1);
    if (!family) break;
    selected.push(family);
    usedDimensions.add(family.dimensionKey);
  }

  return selected.map((family) => presentFamily(data, family));
}

function groupPriorityIssues(
  data: OverviewResponse,
  issues: Issue[],
): FamilyAccumulator[] {
  const grouped = new Map<string, FamilyAccumulator>();
  for (const issue of rankIssuesForClientPriority(data, issues)) {
    const key = familyKey(issue);
    const existing = grouped.get(key);
    if (existing) {
      existing.issues.push(issue);
      continue;
    }
    grouped.set(key, {
      key,
      issues: [issue],
      representative: issue,
      dimensionKey: dimensionKey(issue),
      priority: presentationPriorityForIssue(data, issue, issues),
    });
  }
  return [...grouped.values()].sort(
    (left, right) =>
      compareIssuePresentationPriority(left.priority, right.priority) ||
      left.key.localeCompare(right.key),
  );
}

function familyKey(issue: Issue): string {
  const originCondition = originConditionFact(issue)?.condition;
  if (
    issue.originResourceKind === ORIGIN_FILE_KIND.ROBOTS &&
    originCondition === ORIGIN_CONDITION.AI_CRAWLER_BLOCK
  ) {
    return "origin:robots:ai-crawler-access";
  }
  if (
    issue.originResourceKind === ORIGIN_FILE_KIND.LLMS &&
    originCondition === ORIGIN_CONDITION.MISSING_RESOURCE
  ) {
    return "origin:llms:missing";
  }
  return clientPriorityFamilyKey(issue);
}

function dimensionKey(issue: Issue): string {
  const subcriterion = issue.primarySubcriterionKey
    ? contentFitnessSubcriterionForKey(issue.primarySubcriterionKey)
    : null;
  return subcriterion?.parentMetricKey ?? issue.metricKey;
}

function similarlyImportant(
  strongest: FamilyAccumulator,
  candidate: FamilyAccumulator,
): boolean {
  return (
    strongest.priority.openRank === candidate.priority.openRank &&
    strongest.priority.severityRank === candidate.priority.severityRank &&
    strongest.priority.withinSeverityScore -
      candidate.priority.withinSeverityScore <=
      DIVERSITY_SCORE_WINDOW
  );
}

function presentFamily(
  data: OverviewResponse,
  family: FamilyAccumulator,
): SummaryPriorityFamily {
  const representative = family.representative;
  return {
    key: family.key,
    title: familyTitle(family),
    severity: representative.severity,
    representative,
    relatedIssueCount: family.issues.length,
    dimension: issueDimensionLabel(representative),
    scope: familyScope(data, family.issues),
    action: suggestedActionForIssueInSite(data, representative),
  };
}

function familyTitle(family: FamilyAccumulator): string {
  if (family.key === "origin:robots:ai-crawler-access") {
    return "Review origin-wide AI crawler access";
  }
  if (family.key === "origin:llms:missing") {
    return "Publish an /llms.txt origin guide";
  }
  return clientIssueTitle(family.representative);
}

function familyScope(data: OverviewResponse, issues: Issue[]): string {
  const pages = new Set(
    issues.flatMap((issue) =>
      uniqueCurrentPagesForIssue(data, issue).map((page) => page.id),
    ),
  );
  const resourceKind = issues[0]?.originResourceKind ?? null;
  if (
    resourceKind &&
    issues.every((issue) => issue.originResourceKind === resourceKind)
  ) {
    const path = ORIGIN_RESOURCE_PATHS[resourceKind];
    return pages.size > 0
      ? `${path} origin resource / evidence from ${pages.size} ${pages.size === 1 ? "page" : "pages"}`
      : `${path} origin resource`;
  }
  return `${pages.size} affected ${pages.size === 1 ? "page" : "pages"}`;
}
